import axios from "axios";

import * as asn1js from "asn1js";
import * as pkijs from "pkijs";

const REQUEST_HEADER = "-----BEGIN CERTIFICATE REQUEST-----\r\n"
const REQUEST_FOOTER = "-----END CERTIFICATE REQUEST-----\r\n"

const PRIVATE_HEADER = "-----BEGIN PRIVATE KEY-----\r\n"
const PRIVATE_FOOTER = "-----END PRIVATE KEY-----\r\n"

const BASE_URI = 'https://control.vectioneer.com'
const CSR_URI = BASE_URI + ':5551/api/rest/csr'
export const PUBLIC_URI = BASE_URI + ':5551/api/rest/pub'
export const TEST_URI = BASE_URI + ':5552/api/rest/test'

let privateKeyString

let hashAlg = "SHA-256";
let digestAlg = "SHA-1";
let signAlg = "RSASSA-PKCS1-V1_5";

// format string in order to have each line with length equal to 63
function formatPEM(pemString) {
    const stringLength = pemString.length;
    let resultString = "";

    for (let i = 0, count = 0; i < stringLength; i++, count++) {
        if (count > 63) {
            resultString = `${resultString}\r\n`;
            count = 0;
        }

        resultString = `${resultString}${pemString[i]}`;
    }
    return resultString;
}

// create certificate request accordingly PKCS10
export function createPKCS10(payload) {
    let sequence = Promise.resolve();

    const pkcs10 = new pkijs.CertificationRequest();

    let publicKey;
    let privateKey;

    // check for WebCrypto extension at browser
    const crypto = pkijs.getCrypto();
    if (typeof crypto === "undefined") {
        return Promise.reject("No WebCrypto extension found");
    }

    pkcs10.version = 0;

    // CN - commonName
    pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
        type: "2.5.4.3",
        value: new asn1js.Utf8String({value: payload.commonName})
    }));

    // OU - organizationalUnitName
    pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
        type: "2.5.4.11",
        value: new asn1js.Utf8String({value: payload.organizationalUnitName})
    }));

    // O - organizationName
    pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
        type: "2.5.4.10",
        value: new asn1js.Utf8String({value: payload.organizationName})
    }));

    // C - countryName
    pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
        type: "2.5.4.6",
        value: new asn1js.Utf8String({value: payload.countryName})
    }));

    // L - localityName
    pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
        type: "2.5.4.7",
        value: new asn1js.Utf8String({value: payload.localityName})
    }));

    // ST - stateOrProvinceName
    pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
        type: "2.5.4.8",
        value: new asn1js.Utf8String({value: payload.stateOrProvinceName})
    }));

    let namesPayload = []

    let clearAddresses = function (source) {
        let result = source
        result = result.replace(' ', '')
        result = result.replace(';', ',')
        result = result.replace('/r/n', ',')
        result = result.replace('/n', ',')
        result = result.split(',')
        return result
    }

    // dns
    clearAddresses(payload.dnsString).forEach(dnsName => {
        namesPayload.push(new pkijs.GeneralName({
            type: 2, // dNSName
            value: dnsName.trim()
        }))
    })

    // ip
    clearAddresses(payload.ipString).forEach(ipAddress => {
        namesPayload.push(new pkijs.GeneralName({
            type: 7, // iPAddress
            value: new asn1js.OctetString({valueHex: (new Uint8Array(ipAddress.trim().split('.'))).buffer})
        }))
    })

    const altNames = new pkijs.GeneralNames({
        names: namesPayload
    });

    // key attributes
    pkcs10.attributes = [];

    // generate key
    sequence = sequence.then(() => {
            const algorithm = pkijs.getAlgorithmParameters(signAlg, "generatekey");
            if ("hash" in algorithm.algorithm) {
                algorithm.algorithm.hash.name = hashAlg;
            }
            return crypto.generateKey(algorithm.algorithm, true, algorithm.usages);
        }
    )

    // extract keys
    sequence = sequence.then(keyPair => {
            publicKey = keyPair.publicKey;
            privateKey = keyPair.privateKey;
        }, (error) => Promise.reject(`Error during key generation: ${error}`)
    )

    // import public key to request
    sequence = sequence.then(() => pkcs10.subjectPublicKeyInfo.importKey(publicKey));

    // add extensions
    sequence = sequence
        .then(() => crypto.digest({name: digestAlg}, pkcs10.subjectPublicKeyInfo.subjectPublicKey.valueBlock.valueHex))
        .then(result => {
                pkcs10.attributes.push(new pkijs.Attribute({
                    type: "1.2.840.113549.1.9.14", // pkcs-9-at-extensionRequest
                    values: [(new pkijs.Extensions({
                        extensions: [
                            new pkijs.Extension({
                                extnID: "2.5.29.14",
                                critical: false,
                                extnValue: (new asn1js.OctetString({valueHex: result})).toBER(false)
                            }),
                            new pkijs.Extension({
                                extnID: "2.5.29.17",
                                critical: false,
                                extnValue: altNames.toSchema().toBER(false)
                            })
                        ]
                    })).toSchema()]
                }));
            }
        );

    // sign request
    sequence = sequence.then(() => pkcs10.sign(privateKey, hashAlg), (error) => Promise.reject(`Error signing PKCS#10: ${error}`))

    // convert private key to string
    sequence = sequence
        .then(() => crypto.exportKey("pkcs8", privateKey), error => Promise.reject(`Error during exporting private key: ${error}`))
        .then(privateKeyBuffer => {
            privateKeyString = String.fromCharCode.apply(null, new Uint8Array(privateKeyBuffer))
        })

    // send request and get back signed public key
    sequence = sequence
        .then(() => {
                const certificateString = String.fromCharCode.apply(null, new Uint8Array(pkcs10.toSchema().toBER(false)));
                return axios.post(CSR_URI,
                    `${REQUEST_HEADER}${formatPEM(window.btoa(certificateString))}\r\n${REQUEST_FOOTER}`,
                    {headers: {'Content-Type': 'text/plain'}});
            }, (error) => Promise.reject(`Error during signing request at server: ${error}`)
        )

    // TODO check for network request errors?
    // TODO verify response? 200 оr 400

    // collect public and private keys to PEM
    sequence = sequence
        .then((signedRequest) =>
            Promise.resolve(`${signedRequest.data}${PRIVATE_HEADER}${formatPEM(window.btoa(privateKeyString))}\r\n${PRIVATE_FOOTER}\r\n`)
        );

    return sequence
}


